(ns nosco.ui.tooltips
  (:require
   ["react" :as React]
   ["react-useportal$default" :as usePortal]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.react :as nosco-react]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.util :as util]))

(defn interpose-br
  "Returns a React element with the items of `coll` interposed with `<br>` elements."
  [coll]
  (hx/f (into [:<>] (interpose [:br]) coll)))

(defnc Tooltip [{:keys [label popper visible tooltip-style]}]
  (let [style (.. (:popper popper) -styles -popper)
        ;; style is read-only
        style (if tooltip-style
                (js/Object.assign (clj->js tooltip-style) style)
                style)
        style (if (and visible label)
                (js/Object.assign #js{"display" "block"} style) ;; use display:none to ensure off-screen tooltips don't
                (js/Object.assign #js{"display" "none"} style))] ;; contribute to the document width (e.g. in tabs)
    [(.-Portal (usePortal #js {:bindTo (hooks/useContext popup/OverlayContainerContext)}))
     [:div
      (merge
       {:ref (:popper-ref-cb popper)
        :class ["nosco-tooltip"]
        :style style}
       (js->clj (.. (:popper popper) -attributes -popper) :keywordize-keys true))
      [text/Text {:variant text/Body-Md-Regular}
       label]]]))

(def default-popper-opts
  {:placement "bottom"
   :flip true
   :offset [0 6]})

(defn use-tooltip
  "Returns a vector of [tooltip-props trigger-ref], where tooltip-props is a map of props
  to be passed to `Tooltip` and trigger-ref is a ref to be passed to the element which
  will trigger the tooltip on hover."
  ([] (use-tooltip nil))
  ([{:keys [popper-opts show-hide-delay-ms show-if-hovered-on-mount?]
     :or {show-hide-delay-ms 250}}]
   (let [[visible ->visible] (hooks/useState false)
         ;; :open disables the scroll/resize listeners while the tooltip is hidden,
         ;; so hidden tooltips (there can be hundreds mounted at once) stop
         ;; repositioning on every scroll tick.
         [trigger-popper-ref-cb popper popper-update] (popup/use-popper (assoc (or popper-opts default-popper-opts) :open visible))
         mouse-inside-ref (React/useRef false)
         trigger-ref (React/useRef nil)
         mounted? (util/use-mounted?)
         update-visibility-fn (util/use-debounce
                               show-hide-delay-ms
                               (fn []
                                 (when (mounted?)
                                   (->visible (.-current mouse-inside-ref)))))]
     (hooks/useLayoutEffect
      (fn []
        (when popper-update
          (popper-update))
        js/undefined)
      [visible])
     (hooks/useEffect
      (fn []
        (when-let [trigger-el (.-current trigger-ref)]
          (let [mouse-enter-fn (fn []
                                 (set! (.-current mouse-inside-ref) true)
                                 (update-visibility-fn))
                mouse-leave-fn (fn []
                                 (set! (.-current mouse-inside-ref) false)
                                 (update-visibility-fn))]
            (doto trigger-el
              (.addEventListener "mouseenter" mouse-enter-fn)
              (.addEventListener "mouseleave" mouse-leave-fn))
            (fn []
              (doto trigger-el
                (.removeEventListener "mouseenter" mouse-enter-fn)
                (.removeEventListener "mouseleave" mouse-leave-fn)))))))
     ;; Used by `WithTooltip`'s lazy path: the tooltip machinery is only mounted
     ;; on the first hover, by which point the `mouseenter` that triggered it has
     ;; already fired. Re-check the live :hover state on mount so the tooltip shows
     ;; if the pointer is still over the trigger — and does NOT get stuck showing
     ;; if the pointer merely passed through during the mount.
     (hooks/useEffect
      (fn []
        (when (and show-if-hovered-on-mount?
                   (some-> (.-current trigger-ref) (.matches ":hover")))
          (set! (.-current mouse-inside-ref) true)
          (update-visibility-fn))
        js/undefined)
      [])
     [{:visible visible :popper popper}
      (nosco-react/merge-refs trigger-ref trigger-popper-ref-cb)])))

(defnc ^:private ArmedTooltip
  "The heavy half of `WithTooltip`, mounted only once the trigger has been hovered.
  Owns the actual Popper instance and renders the `Tooltip`."
  [{:keys [label popper-opts show-hide-delay-ms tooltip-style children]
    :or {show-hide-delay-ms 250}}]
  (let [[tooltip-props trigger-ref] (use-tooltip {:popper-opts popper-opts
                                                  :show-hide-delay-ms show-hide-delay-ms
                                                  :show-if-hovered-on-mount? true})]
    [:<>
     (React/cloneElement children #js {:ref trigger-ref})
     [Tooltip (assoc tooltip-props
                     :label label
                     :tooltip-style tooltip-style)]]))

(defnc WithTooltip
  "Takes a single element as children and uses that element as the trigger for a tooltip.
  If label is falsey, then the children are simply passed through.
  Throws with multiple or no children.

  On devices whose pointer can hover (desktop) the tooltip is lazily mounted: until the
  trigger is first hovered we only attach a lightweight `mouseenter` listener and render
  the bare children — no Popper instance and no `Tooltip`. On the first hover we mount
  `ArmedTooltip`, which stays mounted afterwards so subsequent hovers are instant.

  On devices without hover (touch) the lazy path is skipped and `ArmedTooltip` is mounted
  eagerly, since we can't rely on `mouseenter` to arm it."
  [{:keys [label popper-opts show-hide-delay-ms tooltip-style children]
    :or {show-hide-delay-ms 250}}]
  {:pre [(React/isValidElement children)]}
  (let [hover? (util/use-media-query "(hover: hover)")
        has-label? (boolean label)
        [armed? set-armed!] (hooks/useState false)
        trigger-ref (React/useRef nil)]
    (hooks/useEffect
     (fn []
       (when (and hover? has-label? (not armed?))
         (when-let [el (.-current trigger-ref)]
           (let [on-enter (fn [] (set-armed! true))]
             (.addEventListener el "mouseenter" on-enter)
             (fn [] (.removeEventListener el "mouseenter" on-enter))))))
     [hover? has-label? armed?])
    (cond
      (not has-label?) children
      (or armed? (not hover?)) [ArmedTooltip {:label label
                                              :popper-opts popper-opts
                                              :show-hide-delay-ms show-hide-delay-ms
                                              :tooltip-style tooltip-style}
                                children]
      :else (React/cloneElement children #js {:ref trigger-ref}))))
